/**
 * AdminUsers.tsx — /app/admin/users  (v9.3.0)
 *
 * Admin-only workspace. Lists all users and exposes mutation controls.
 *
 * ── Access control ───────────────────────────────────────────────────────────
 * Double-gated:
 *   1. Route level — redirects non-admins away
 *   2. Backend   — /api/admin/users* enforces role='admin'
 *
 * ── Mutations (v9.3.0) ───────────────────────────────────────────────────────
 * Each row exposes two conditional action buttons:
 *   ENABLE / DISABLE — toggles is_active
 *   PROMOTE / DEMOTE — toggles role between 'user' and 'admin'
 *
 * Safety rails (enforced by both backend and UI):
 *   - Admins cannot act on their own row → buttons hidden
 *   - Last active admin is locked → buttons hidden when applicable
 *
 * Destructive actions (disable, demote) require confirmation via window.confirm.
 * Errors from the backend appear in a transient banner at the top of the table.
 */

import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useAdminUsers, type AdminUserRow } from '../../features/admin/hooks/useAdminUsers';

// ── Design tokens (match Portfolio.tsx palette) ───────────────────────────────
const C = {
  gold:   '#C9A84C',
  border: '#1A3050',
  card:   'rgba(13,31,53,0.55)',
  text:   '#E8EDF5',
  dim:    '#4A6FA5',
  muted:  '#3A5070',
  green:  '#4ADE80',
  red:    '#F87171',
  amber:  '#FCD34D',
  blue:   '#38BDF8',
  purple: '#A78BFA',
  mono:   "'Courier New', monospace",
};

// ── Formatting helpers ────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

function roleColor(role: string): string {
  return role === 'admin' ? C.purple : C.dim;
}

function tierColor(tier: string): string {
  return tier === 'premium' ? C.gold
       : tier === 'byob'    ? C.blue
       :                      C.dim;
}

function reasonLabel(reason: string | undefined): string {
  switch (reason) {
    case 'SELF_ACTION_FORBIDDEN': return 'You cannot modify your own account.';
    case 'LAST_ADMIN_LOCKED':     return 'Cannot remove the last active admin.';
    case 'NOT_FOUND':             return 'User no longer exists.';
    case 'FORBIDDEN':             return 'Access denied.';
    case 'NOT_AUTHENTICATED':     return 'Session expired. Please log in again.';
    case 'NETWORK_ERROR':         return 'Network error. Try again.';
    case 'VALIDATION_ERROR':      return 'Invalid request.';
    default:                      return reason ? `Error: ${reason}` : 'Unknown error.';
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export function AdminUsers(): JSX.Element {
  const { entitlement, status: authStatus, user: currentUser } = useAuth();

  if (authStatus === 'loading') {
    return (
      <div style={{ padding: 40, color: C.dim, fontFamily: C.mono, fontSize: 11 }}>
        LOADING AUTHENTICATION…
      </div>
    );
  }

  if (!entitlement.isAdmin) {
    return <Navigate to="/app" replace />;
  }

  return <AdminUsersContent currentUserId={currentUser?.id ?? -1} />;
}

// ── Content ───────────────────────────────────────────────────────────────────

function AdminUsersContent({ currentUserId }: { currentUserId: number }): JSX.Element {
  const { status, users, error, reload, setActive, setRole } = useAdminUsers();
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId,      setBusyId]      = useState<number | null>(null);

  // Count active admins — used for last-admin guard in the UI
  const activeAdminCount = users.filter(u => u.role === 'admin' && u.isActive).length;

  const handleToggleActive = async (u: AdminUserRow) => {
    const nextActive = !u.isActive;
    if (!nextActive) {
      if (!window.confirm(`Disable ${u.email}? They will not be able to log in. Existing sessions may remain valid until they expire (up to 8 hours).`)) return;
    }
    setActionError(null);
    setBusyId(u.id);
    const result = await setActive(u.id, nextActive);
    setBusyId(null);
    if (!result.ok) setActionError(reasonLabel(result.reason));
  };

  const handleToggleRole = async (u: AdminUserRow) => {
    const nextRole = u.role === 'admin' ? 'user' : 'admin';
    const confirmMsg = nextRole === 'admin'
      ? `Promote ${u.email} to admin? They will gain full administrative access.`
      : `Demote ${u.email} from admin to user? They will lose administrative access.`;
    if (!window.confirm(confirmMsg)) return;
    setActionError(null);
    setBusyId(u.id);
    const result = await setRole(u.id, nextRole);
    setBusyId(null);
    if (!result.ok) setActionError(reasonLabel(result.reason));
  };

  return (
    <div style={{ padding: '20px 24px', color: C.text, fontFamily: C.mono }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 20 }}>
        <h1 style={{ fontSize: 14, fontWeight: 700, letterSpacing: 2, color: C.gold, margin: 0 }}>
          ADMIN · USERS
        </h1>
        <span style={{ fontSize: 9, color: C.dim, letterSpacing: 1 }}>
          {status === 'ready' ? `${users.length} user${users.length === 1 ? '' : 's'} · ${activeAdminCount} active admin${activeAdminCount === 1 ? '' : 's'}` : ''}
        </span>
        <button
          onClick={reload}
          style={{
            marginLeft: 'auto',
            fontSize: 9,
            letterSpacing: 1,
            padding: '4px 10px',
            background: 'transparent',
            color: C.dim,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            cursor: 'pointer',
            fontFamily: C.mono,
            textTransform: 'uppercase',
          }}
        >
          Refresh
        </button>
      </div>

      {/* Action error banner */}
      {actionError && (
        <div style={{
          padding: '8px 12px',
          marginBottom: 12,
          background: `${C.red}15`,
          border: `1px solid ${C.red}40`,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ fontSize: 9, letterSpacing: 1.5, color: C.red, fontWeight: 700 }}>ERROR</span>
          <span style={{ fontSize: 11, color: C.text }}>{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: C.dim,
              cursor: 'pointer',
              fontSize: 14,
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Loading */}
      {status === 'loading' && (
        <div style={{ padding: 20, color: C.dim, fontSize: 11 }}>
          LOADING USERS…
        </div>
      )}

      {/* Error */}
      {status === 'error' && (
        <div style={{ padding: 16, background: C.card, border: `1px solid ${C.red}40`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 1.5, color: C.red, marginBottom: 4 }}>
            ERROR
          </div>
          <div style={{ fontSize: 11, color: C.text }}>
            {reasonLabel(error ?? undefined)}
          </div>
        </div>
      )}

      {/* Users table */}
      {status === 'ready' && users.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: 'rgba(26,48,80,0.5)' }}>
                <Th>ID</Th>
                <Th>EMAIL</Th>
                <Th>ROLE</Th>
                <Th>TIER</Th>
                <Th>ACTIVE</Th>
                <Th>CREATED</Th>
                <Th>LAST LOGIN</Th>
                <Th>ACTIONS</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isLast={i === users.length - 1}
                  currentUserId={currentUserId}
                  activeAdminCount={activeAdminCount}
                  busy={busyId === u.id}
                  onToggleActive={() => handleToggleActive(u)}
                  onToggleRole={() => handleToggleRole(u)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status === 'ready' && users.length === 0 && (
        <div style={{ padding: 20, color: C.dim, fontSize: 11 }}>
          No users found.
        </div>
      )}

      <div style={{ marginTop: 16, fontSize: 9, color: C.muted, letterSpacing: 0.5, lineHeight: 1.6 }}>
        Disabling a user prevents new logins but may not immediately invalidate existing sessions.
        Demoting the last active admin is blocked to preserve admin access.
      </div>
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

interface UserRowProps {
  user:             AdminUserRow;
  isLast:           boolean;
  currentUserId:    number;
  activeAdminCount: number;
  busy:             boolean;
  onToggleActive:   () => void;
  onToggleRole:     () => void;
}

function UserRow({
  user,
  isLast,
  currentUserId,
  activeAdminCount,
  busy,
  onToggleActive,
  onToggleRole,
}: UserRowProps): JSX.Element {
  const borderBottom = isLast ? 'none' : `1px solid ${C.border}`;

  // Is this the current user?
  const isSelf = user.id === currentUserId;

  // Would removing this user leave zero active admins?
  const isLastActiveAdmin = user.role === 'admin' && user.isActive && activeAdminCount <= 1;

  // Compute button visibility
  const showActiveBtn = !isSelf && !(isLastActiveAdmin && user.isActive);
  const showRoleBtn   = !isSelf && !(isLastActiveAdmin && user.role === 'admin');

  return (
    <tr>
      <Td borderBottom={borderBottom}><span style={{ color: C.dim }}>{user.id}</span></Td>
      <Td borderBottom={borderBottom}>
        {user.email}
        {isSelf && (
          <span style={{ marginLeft: 6, fontSize: 8, color: C.gold, letterSpacing: 1 }}>
            (YOU)
          </span>
        )}
      </Td>
      <Td borderBottom={borderBottom}>
        <Badge color={roleColor(user.role)}>{user.role.toUpperCase()}</Badge>
      </Td>
      <Td borderBottom={borderBottom}>
        <Badge color={tierColor(user.tier)}>{user.tier.toUpperCase()}</Badge>
      </Td>
      <Td borderBottom={borderBottom}>
        <span style={{ color: user.isActive ? C.green : C.red }}>
          {user.isActive ? 'YES' : 'NO'}
        </span>
      </Td>
      <Td borderBottom={borderBottom}>
        <span style={{ color: C.dim }}>{formatDate(user.createdAt)}</span>
      </Td>
      <Td borderBottom={borderBottom}>
        <span style={{ color: user.lastLoginAt ? C.text : C.muted }}>
          {formatDate(user.lastLoginAt)}
        </span>
      </Td>
      <Td borderBottom={borderBottom}>
        <div style={{ display: 'flex', gap: 6 }}>
          {showActiveBtn && (
            <ActionButton
              disabled={busy}
              color={user.isActive ? C.amber : C.green}
              onClick={onToggleActive}
            >
              {user.isActive ? 'Disable' : 'Enable'}
            </ActionButton>
          )}
          {showRoleBtn && (
            <ActionButton
              disabled={busy}
              color={user.role === 'admin' ? C.dim : C.purple}
              onClick={onToggleRole}
            >
              {user.role === 'admin' ? 'Demote' : 'Promote'}
            </ActionButton>
          )}
          {!showActiveBtn && !showRoleBtn && (
            <span style={{ color: C.muted, fontSize: 9, letterSpacing: 0.5 }}>—</span>
          )}
        </div>
      </Td>
    </tr>
  );
}

// ── Cell helpers ──────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <th
      style={{
        padding: '8px 12px',
        textAlign: 'left',
        fontSize: 9,
        letterSpacing: 1.5,
        color: C.dim,
        fontWeight: 600,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, borderBottom }: { children: React.ReactNode; borderBottom: string }): JSX.Element {
  return <td style={{ padding: '10px 12px', borderBottom }}>{children}</td>;
}

function Badge({ color, children }: { color: string; children: React.ReactNode }): JSX.Element {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: 1,
        color,
        border: `1px solid ${color}40`,
        background: `${color}15`,
        padding: '1px 6px',
        borderRadius: 2,
      }}
    >
      {children}
    </span>
  );
}

function ActionButton({
  disabled,
  color,
  onClick,
  children,
}: {
  disabled: boolean;
  color:    string;
  onClick:  () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 9,
        letterSpacing: 0.8,
        padding: '3px 8px',
        background: 'transparent',
        color: disabled ? C.muted : color,
        border: `1px solid ${disabled ? C.muted : color}50`,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: C.mono,
        textTransform: 'uppercase',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
